/**
 * Company alerts in the account menu (ADR-022):
 *
 *   - Company seat invitations: someone offered the viewer an owner or admin
 *     seat. Nothing is held until they accept, which needs MFA like every
 *     Company route; Decline answers it for good.
 *   - Link notices: a workspace the viewer belongs to joined a Company, so
 *     arches that Company approves can now read what the viewer can see
 *     there. Told to every member, not only the owner who accepted;
 *     dismissed per viewer.
 *   - Arche releases (ADR-026): a Company offered to hand one of its arches
 *     to the viewer. Accepting makes them its owner and the one paid its
 *     earnings; nothing moves until they do.
 *   - Arche offers to a Company the viewer OWNS (ADR-026): a creator offered
 *     their arche to the Company. The decision needs the acceptance
 *     disclosure, so this only points at Company → Owned arches; it is not
 *     answered here.
 *
 * Showing the invitations and proposals marks them seen (the header dot
 * clears); notices count until dismissed. Renders nothing when there is
 * nothing to say.
 */

import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import { Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import type { CompanySeatInvitationForMeDto } from '@/lib/api';
import { useAuth } from '../hooks/useAuth';
import {
  useAcceptCompanySeatInvitation,
  useDeclineCompanySeatInvitation,
  useMyCompanySeatInvitations,
} from '../hooks/useCompanies';
import { useDecideArcheRelease, useMyArcheTransfers } from '../hooks/useArcheGovernance';
import {
  dismissCompanyLinkNotice,
  markInvitationsSeen,
  useCompanyLinkNotices,
} from '../hooks/useInvitationAlerts';
import { describeCompanyError } from '../lib/company-errors';
import { companySectionPath } from '../pages/company/shared';

function inviterLabel(inv: CompanySeatInvitationForMeDto): string {
  const by = inv.invitedBy;
  return by.displayName ?? (by.username ? `@${by.username}` : 'Someone');
}

/** Accept / Decline for each open seat invitation. Shared by the menu and the Company page. */
export function CompanySeatInvitationsList({ compact = false }: { compact?: boolean }) {
  const { isAuthenticated, isGuest } = useAuth();
  const query = useMyCompanySeatInvitations({ enabled: isAuthenticated && !isGuest });
  const accept = useAcceptCompanySeatInvitation();
  const decline = useDeclineCompanySeatInvitation();
  const list = query.data ?? [];

  useEffect(() => {
    const ids = (query.data ?? []).map((i) => i.invitationId);
    if (ids.length > 0) markInvitationsSeen(ids);
  }, [query.data]);

  if (list.length === 0) return null;

  async function handleAccept(inv: CompanySeatInvitationForMeDto): Promise<void> {
    try {
      await accept.mutateAsync(inv.invitationId);
      toast({
        title: `You are now ${inv.role === 'owner' ? 'an owner' : 'an admin'} of ${inv.companyName}`,
      });
    } catch (err) {
      toast({
        title: 'Could not accept',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  async function handleDecline(inv: CompanySeatInvitationForMeDto): Promise<void> {
    try {
      await decline.mutateAsync(inv.invitationId);
      toast({ title: `Declined ${inv.companyName}’s invitation` });
    } catch (err) {
      toast({
        title: 'Could not decline',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <ul className="space-y-2">
      {list.map((inv) => (
        <li
          key={inv.invitationId}
          className={
            compact
              ? 'space-y-1.5 text-xs'
              : 'flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm'
          }
        >
          <p className="text-foreground">
            {inviterLabel(inv)} invited you to be {inv.role === 'owner' ? 'an owner' : 'an admin'}{' '}
            of <span className="font-medium">{inv.companyName}</span>. Company roles manage billing,
            links and approvals; they give no access to any workspace&apos;s data.
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className={compact ? 'h-7 px-2 text-xs' : undefined}
              onClick={() => void handleAccept(inv)}
              disabled={accept.isPending || decline.isPending}
              aria-label={`Accept ${inv.companyName}’s invitation`}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={compact ? 'h-7 px-2 text-xs' : undefined}
              onClick={() => void handleDecline(inv)}
              disabled={accept.isPending || decline.isPending}
              aria-label={`Decline ${inv.companyName}’s invitation`}
            >
              Decline
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** "<workspace> joined <Company>" notices, each dismissible by this viewer. */
export function CompanyLinkNoticesList() {
  const notices = useCompanyLinkNotices();
  if (notices.length === 0) return null;
  return (
    <ul className="space-y-2">
      {notices.map((n) => (
        <li key={n.invitationId} className="flex items-start gap-2 text-xs">
          <p className="flex-1 text-foreground">
            <span className="font-medium">{n.holderDisplayName}</span> joined{' '}
            <span className="font-medium">{n.companyName}</span>. Arches the Company approves can
            now read what you can see there.
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 shrink-0 p-0"
            onClick={() => dismissCompanyLinkNotice(n.invitationId)}
            aria-label={`Dismiss: ${n.holderDisplayName} joined ${n.companyName}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** Arches a Company offered to hand to the viewer (ADR-026): Accept / Decline. */
export function ArcheReleaseOffersList() {
  const { isAuthenticated, isGuest } = useAuth();
  const query = useMyArcheTransfers({ enabled: isAuthenticated && !isGuest });
  const decide = useDecideArcheRelease();
  // Accepting makes the viewer the arche's payer as well as its owner, so it
  // asks first, like the other two acceptances; declining is one click.
  const [confirming, setConfirming] = useState<string | null>(null);
  const offers = (query.data?.transfers ?? []).filter((t) => t.direction === 'release_to_user');
  // Keyed on the ids, not the array identity the filter makes each render.
  const offerIds = offers.map((t) => t.transferId).join(',');

  useEffect(() => {
    if (offerIds) markInvitationsSeen(offerIds.split(','));
  }, [offerIds]);

  if (offers.length === 0) return null;
  const confirmingOffer = offers.find((t) => t.transferId === confirming) ?? null;

  async function answer(transferId: string, decision: 'accept' | 'decline'): Promise<void> {
    try {
      await decide.mutateAsync({ transferId, decision });
      toast({ title: decision === 'accept' ? 'The arche is yours now' : 'Declined' });
    } catch (err) {
      toast({
        title: 'Could not answer',
        description: describeCompanyError(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <ul className="space-y-2">
      {offers.map((t) => {
        const arche = t.archeName ?? 'one of its arches';
        return (
          <li key={t.transferId} className="space-y-1.5 text-xs">
            <p className="text-foreground">
              <span className="font-medium">{t.companyName ?? 'A Company'}</span> offered to hand
              you <span className="font-medium">{arche}</span>. If you accept, you own it, you are
              paid its earnings and you pay its per-arche costs (asset library, content packs,
              generated docs), and you manage its team; the Company no longer does.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={decide.isPending}
                aria-label={`Accept ${arche} from ${t.companyName ?? 'the Company'}`}
                onClick={() => setConfirming(t.transferId)}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={decide.isPending}
                aria-label={`Decline ${arche} from ${t.companyName ?? 'the Company'}`}
                onClick={() => void answer(t.transferId, 'decline')}
              >
                Decline
              </Button>
            </div>
          </li>
        );
      })}
      <AlertDialog open={confirming !== null} onOpenChange={(o) => !o && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingOffer?.archeName
                ? `Take ownership of “${confirmingOffer.archeName}”?`
                : 'Take ownership of this arche?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  From now on its earnings are paid to you, and its per-arche costs — the asset
                  library, content packs and generated docs — are charged to you. You manage its
                  team; the Company no longer does, and only you could hand it back.
                </p>
                <p>
                  Who can use it stays as the Company left it — including any access given to the
                  Company or its workspaces. Review it on the arche&apos;s Access panel afterwards.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = confirming;
                setConfirming(null);
                if (id) void answer(id, 'accept');
              }}
            >
              Accept the arche
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ul>
  );
}

/**
 * Arches offered to a Company the viewer owns (ADR-026). The acceptance
 * screen lives on Company → Owned arches (it discloses the payer and every
 * collaborator), so this points there rather than deciding in a menu.
 */
export function CompanyArcheOffersList() {
  const { isAuthenticated, isGuest } = useAuth();
  const query = useMyArcheTransfers({ enabled: isAuthenticated && !isGuest });
  const offers = query.data?.offersToMyCompanies ?? [];
  const offerIds = offers.map((t) => t.transferId).join(',');

  useEffect(() => {
    if (offerIds) markInvitationsSeen(offerIds.split(','));
  }, [offerIds]);

  if (offers.length === 0) return null;
  return (
    <ul className="space-y-2">
      {offers.map((t) => {
        const by = t.proposedBy;
        const who = by.displayName ?? (by.username ? `@${by.username}` : 'Someone');
        return (
          <li key={t.transferId} className="space-y-1.5 text-xs">
            <p className="text-foreground">
              <span className="font-medium">{who}</span> offered{' '}
              <span className="font-medium">{t.archeName ?? 'an arche'}</span> to{' '}
              <span className="font-medium">{t.companyName ?? 'your Company'}</span>. An owner
              decides; the offer lapses on {new Date(t.expiresAt).toLocaleDateString()}.
            </p>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" asChild>
              <Link to={companySectionPath(t.companyId, 'arches')}>
                Review on Company → Owned arches
              </Link>
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

/** The account menu's Company alerts block; nothing when there is nothing to say. */
export function AccountAlerts() {
  const { isAuthenticated, isGuest } = useAuth();
  const seatInvitations = useMyCompanySeatInvitations({ enabled: isAuthenticated && !isGuest });
  const notices = useCompanyLinkNotices();
  const transfers = useMyArcheTransfers({ enabled: isAuthenticated && !isGuest });
  const releaseOffers = (transfers.data?.transfers ?? []).filter(
    (t) => t.direction === 'release_to_user',
  );
  const companyOffers = transfers.data?.offersToMyCompanies ?? [];
  if (
    (seatInvitations.data?.length ?? 0) === 0 &&
    notices.length === 0 &&
    releaseOffers.length === 0 &&
    companyOffers.length === 0
  ) {
    return null;
  }
  return (
    <section
      aria-label="Company alerts"
      className="space-y-2 border-b border-border bg-primary/5 p-3"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Building2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        Company
      </p>
      <CompanySeatInvitationsList compact />
      <CompanyLinkNoticesList />
      <ArcheReleaseOffersList />
      <CompanyArcheOffersList />
    </section>
  );
}
